import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

// SectionCard — card with optional icon-circle header + title + subtitle + body.
// Visual baseline: rounded-lg border bg-bg-muted (default) p-5 with a 9x9 brand-50
// circle around an icon, title (text-base font-semibold), subtitle (text-xs muted),
// optional `action` slot on the right (e.g. a save button).
//
// Props:
//   - icon: ReactNode (e.g. <Sparkles className="w-4 h-4" />)
//   - title: ReactNode
//   - subtitle?: ReactNode
//   - action?: ReactNode (right-aligned in the header row)
//   - children: body
//   - className?: string
//   - bodyClassName?: string
//   - tone?: 'bg' | 'bg-muted'  default 'bg-muted'

const TONES = {
  bg: 'bg-bg',
  'bg-muted': 'bg-bg-muted',
};

export const SectionCard = forwardRef(function SectionCard(
  {
    icon,
    title,
    subtitle,
    action,
    children,
    className,
    bodyClassName,
    tone = 'bg-muted',
    ...rest
  },
  ref,
) {
  return (
    <section
      ref={ref}
      className={cn(
        // Hook semántico para la capa global de densidad compacta
        // (_density-compact.scss, activa con viewport bajo + puntero fino).
        'ui-section-card',
        'rounded-lg border border-border p-5 space-y-4 shadow-card',
        TONES[tone] ?? TONES['bg-muted'],
        className,
      )}
      {...rest}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          {icon && (
            <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            {title && (
              <h3 className="text-base font-semibold text-fg">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-fg-muted mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children != null && (
        bodyClassName ? <div className={bodyClassName}>{children}</div> : <div>{children}</div>
      )}
    </section>
  );
});

SectionCard.displayName = 'SectionCard';

export default SectionCard;
